import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ProductGroup, UpdateProductGroupRequestBody } from "./productGroupsApi";
import { addProductGroup, deleteProductGroup, listProductGroups, updateProductGroup } from "./productGroupsApi";
import { useSnackbar } from "notistack";
import sortBy from "lodash/sortBy";

const groupsKey = "product-groups-dictionary";

export const useProductGroups = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: [groupsKey],
    queryFn: ({ signal }) => listProductGroups(signal),
  });

  const addMutation = useMutation<ProductGroup, unknown, Omit<ProductGroup, "id">>({
    mutationFn: (r) => addProductGroup(r),
    onSuccess: async (data) => {
      enqueueSnackbar(`Grupa produktów '${data.name}' została dodana do słownika`, { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: [groupsKey] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się dodać grupy produktów do słownika. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const updateMutation = useMutation<ProductGroup, unknown, { id: string; request: UpdateProductGroupRequestBody }>({
    mutationFn: ({ id, request }) => updateProductGroup(id, request),
    onSuccess: async (data) => {
      enqueueSnackbar(`Grupa produktów '${data.name}' została zaktualizowana`, { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: [groupsKey] });
      await queryClient.invalidateQueries({ queryKey: ["products"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować grupy produktów. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const deleteMutation = useMutation<void, unknown, { readonly id: string; readonly force: boolean }>({
    mutationFn: ({ id, force }) => deleteProductGroup(id, force),
    onSuccess: async () => {
      enqueueSnackbar("Grupa produktów została usunięta ze słownika", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: [groupsKey] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się usunąć grupy produktów ze słownika. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  return {
    productGroups: sortBy(data ?? [], ({ name }) => name.trim().toLowerCase()),
    isLoading,
    addProductGroup: (name: string) =>
      addMutation.mutateAsync({
        name,
      }),
    updateProductGroup: (groupId: string, request: UpdateProductGroupRequestBody) =>
      updateMutation.mutateAsync({ id: groupId, request }),
    deleteProductGroup: (id: string, force: boolean) => deleteMutation.mutateAsync({ id, force }),
  };
};
